import { Component } from '@theme/component';
import { turnstileRequired, turnstileToken as solveTurnstile } from '@theme/turnstile';
import { rememberApplied, forgetApplied, appliedAt } from '@theme/apply-memory';

/**
 * Membership application form.
 *
 * Posts to the App Proxy at /apps/invite/apply. A SIGNED-IN applicant is
 * identified by Shopify's SIGNED logged_in_customer_id, not by this form, so
 * the name/email here are descriptive only — they cannot be used to submit on
 * someone else's behalf. Signing in is no longer required to apply.
 *
 * THE THREE STATUSES. D1 stores exactly `pending`, `approved` and `rejected`.
 * pending draws the panel plus its bar at the design's 33; both outcomes draw
 * the panel with the bar REMOVED and the label swapped, because neither is
 * further along the same axis and neither says WHICH way it went. Full
 * reasoning in the Liquid.
 *
 * NOT A NO-JS FALLBACK, whatever the comment here used to say: the App Proxy
 * answers 415 to anything that is not application/json, so the <form> cannot
 * post on its own and this module always intercepts.
 *
 * SIZE: Theme Check reads the RAW bytes of a local asset, not the compressed
 * ones its message names. This file sits close to the ceiling in
 * .theme-check.yml, so keep new prose in the Liquid, not here.
 */
/** Device-local note that a guest applied. Holds a date, never an address. */

class ApplyForm extends Component {
  /** @type {boolean} */
  #busy = false;

  /** @type {Promise<void> | undefined} */
  #ready;

  mount() {
    this.on(this, 'submit', (event) => {
      event.preventDefault();
      this.#submit();
    });
    // A signed-in customer with an application on file gets the "received"
    // state instead of the form. A guest never can: see #checkExisting.
    //
    // HELD, NOT FIRED AND FORGOTTEN: the form is interactive before this
    // resolves, so a REJECTED applicant (who is handed the live form) could
    // submit into a decided application. #submit awaits it. Guests get
    // {status: null} and proceed.
    this.#ready = this.#checkExisting();

    // State / Region follows the country; see #syncState. Synced now as well as
    // on change, because a restored selection fires no `change` event.
    const country = /** @type {HTMLSelectElement | undefined} */ (this.refs['country']);
    if (country) this.on(country, 'change', () => this.#syncState());
    this.#syncState();

    // The way back out of a panel drawn from a device note rather than an
    // identity. Without it, the next person on a shared computer is shown
    // somebody else's state and cannot reach the form at all.
    const restart = /** @type {HTMLElement | undefined} */ (this.refs['restart']);
    restart?.addEventListener('click', () => {
      this.#forget();
      const form = /** @type {HTMLElement | undefined} */ (this.refs['form']);
      const received = /** @type {HTMLElement | undefined} */ (this.refs['received']);
      const routes = /** @type {HTMLElement | undefined} */ (this.refs['guestRoutes']);
      if (received) received.hidden = true;
      if (routes) routes.hidden = true;
      if (form) {
        form.hidden = false;
        /** @type {HTMLInputElement | undefined} */ (this.refs['name'])?.focus();
      }
    });
  }

  /**
   * A GUEST LEAVES NO SERVER-SIDE TRACE, so this page would hand them the form
   * again next visit as though they had never applied. A note on their own
   * device, holding NOTHING but the fact and the date - never the address,
   * which would leave one person's email in the next person's browser.
   */
  #remember() {
    if (this.dataset.membershipState === 'applicant') return;
    rememberApplied();
  }

  #forget() {
    forgetApplied();
  }

  async #checkExisting() {
    try {
      const res = await fetch('/apps/invite/application', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });
      if (!res.ok) return;
      const data = await res.json();
      // A GUEST ALWAYS LANDS HERE WITH NOTHING: the endpoint is customer-keyed
      // on purpose and answers `{ status: null }`, so the form stays visible.
      // That is their expected shape, not a failure.
      const status = data?.application?.status;
      if (status) {
        // The server knows them, so the device note is redundant and stale.
        this.#forget();
        this.#showReceived(status);
        this.#advance(data.application.ack_email_status);
        return;
      }
      this.#restoreGuest();
    } catch {
      this.#restoreGuest();
      // Best-effort: if the check fails the form stays visible. A network error
      // must never blank the page or strand the applicant.
    }
  }

  /**
   * THE SERVER IS ALWAYS RIGHT WHEN IT KNOWS ANYTHING. This speaks only for a
   * visitor it cannot identify — the guest who applied and came back. A note
   * left on this device by somebody else must never speak for a signed-in one.
   */
  #restoreGuest() {
    if (this.dataset.membershipState !== 'guest') return;
    if (!appliedAt()) return;
    this.#showReceived('pending');
    const routes = /** @type {HTMLElement | undefined} */ (this.refs['guestRoutes']);
    if (routes) routes.hidden = false;
  }

  /**
   * Move the bar to "Received" when the acknowledgement email actually ARRIVED.
   *
   * DELIVERY, NOT SENDING, and FORWARD ONLY. Only `delivered` and `opened`
   * advance it; `sent` has not arrived and bounced/dropped/skipped never will,
   * and a bar that retreats reads as an application being un-filed. Numbers and
   * wording come from data attributes, so the stage stays a merchant setting.
   * The full account of both rules is in blocks/apply-form.liquid.
   * @param {string | null | undefined} ackStatus
   */
  #advance(ackStatus) {
    if (ackStatus !== 'delivered' && ackStatus !== 'opened') return;
    const track = /** @type {HTMLElement | undefined} */ (this.refs['progressTrack']);
    const label = /** @type {HTMLElement | undefined} */ (this.refs['progressLabel']);
    const wrap = track?.parentElement;
    if (!track || !wrap) return;

    const value = Number(wrap.dataset.receivedValue);
    const text = wrap.dataset.receivedLabel;
    if (!Number.isFinite(value) || value <= Number(track.getAttribute('aria-valuenow'))) return;

    track.style.setProperty('--apply-progress', value + '%');
    track.setAttribute('aria-valuenow', String(value));
    if (text) {
      // aria-valuetext carries the wording a person needs; the raw percentage is
      // a rendering of the stage, not a measurement anyone can act on.
      track.setAttribute('aria-valuetext', text);
      if (label) label.textContent = text;
    }
  }

  /**
   * @param {string} status `pending` keeps the progress bar; anything else is a
   *   terminal outcome and removes it.
   */
  #showReceived(status) {
    const form = /** @type {HTMLElement | undefined} */ (this.refs['form']);
    const received = /** @type {HTMLElement | undefined} */ (this.refs['received']);
    if (received && status !== 'pending') this.#decided(received);
    if (form) form.hidden = true;
    if (received) received.hidden = false;
  }

  /**
   * Redraw the panel for a closed review. The bar is REMOVED, not zeroed and
   * not filled, so no `aria-valuenow` is left describing a distance that no
   * longer exists. The "still running" copy goes with it, via the data-state
   * rule in the block's stylesheet.
   * @param {HTMLElement} received
   */
  #decided(received) {
    received.dataset.state = 'decided';
    /** @type {HTMLElement | undefined} */ (this.refs['progressTrack'])?.remove();
    const heading = /** @type {HTMLElement | undefined} */ (this.refs['receivedHeading']);
    const label = /** @type {HTMLElement | undefined} */ (this.refs['progressLabel']);
    const { decidedHeading, decidedStatus } = received.dataset;
    if (heading && decidedHeading) heading.textContent = decidedHeading;
    if (label && decidedStatus) label.textContent = decidedStatus;
  }

  async #submit() {
    if (this.#busy) return;

    // Close the window described in mount(). Cheap after the first submit:
    // the promise is already settled, so this yields a microtask and no more.
    await this.#ready;
    const received = /** @type {HTMLElement | undefined} */ (this.refs['received']);
    if (received && !received.hidden) return;

    const name = this.#value('name');
    const email = this.#value('email');
    const message = this.#value('message');
    const gender = this.#value('gender');
    const country = this.#value('country');
    const state = this.#value('state');
    const city = this.#value('city');
    // Matches data-ref="profession" on the input added for 387:3619.
    const profession = this.#value('profession');
    const heardAbout = this.#value('heardAbout');
    const referredBy = this.#value('referredBy');
    const termsEl = /** @type {HTMLInputElement | undefined} */ (this.refs['terms']);
    const termsAccepted = Boolean(termsEl?.checked);

    if (!name || !email) {
      this.#status('Please add your name and email address.', 'error');
      return;
    }
    if (!termsAccepted) {
      this.#status('Please accept the Privacy Policy to apply.', 'error');
      return;
    }

    this.#setBusy(true);
    this.#status('Sending…', 'pending');

    // Wait for the bot-check token before sending — the widget solves
    // asynchronously, so reading it the instant the form submits often finds it
    // empty, which the server rejects. Shared with the other three gated forms:
    // see assets/turnstile.js for the wait and the two loading modes.
    const turnstileToken = await solveTurnstile(this, () =>
      this.#status('Verifying you are human…', 'pending')
    );
    if (turnstileRequired(this) && !turnstileToken) {
      this.#status('We could not verify your browser. Please refresh and try again.', 'error');
      this.#setBusy(false);
      return;
    }

    try {
      const response = await fetch('/apps/invite/apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name, email, message, gender, country, state, city, profession, heardAbout, referredBy, turnstileToken,
          hp: this.#value('hp'),
          // Privacy, not Terms — see blocks/apply-form.liquid. Version set by
          // Liquid on the host dataset, not recomputed here.
          privacyAccepted: termsEl?.checked === true,
          privacyPolicyVersion: this.dataset.privacyVersion || '',
        }),
      });

      // NO 401 BRANCH: the endpoint dropped sign_in_required and guests apply
      // without an account. The only 401 left here is a failed App Proxy
      // signature, a misconfiguration nobody can act on, so it falls to the
      // generic message rather than telling them to sign in for no reason.
      //
      // 403 = the bot check did not pass (usually no token yet, or a browser
      // extension blocked it). Give a recoverable message, not a vague error.
      if (response.status === 403) {
        this.#status('We could not verify your browser. Please refresh and try again.', 'error');
        return;
      }
      // 400 = a validation message from the server (missing field / T&C). Show it.
      if (response.status === 400) {
        let message = 'Please check the form and try again.';
        try { const b = await response.json(); if (b && b.message) message = b.message; } catch {}
        this.#status(message, 'error');
        return;
      }
      if (response.status === 429) {
        this.#status('Too many attempts. Please try again later.', 'error');
        return;
      }
      if (!response.ok) {
        this.#status('Something went wrong. Please try again shortly.', 'error');
        return;
      }

      const result = await response.json();

      if (result.alreadyDecided) {
        // Their application was already reviewed. Resubmitting does not reopen
        // it, and pretending otherwise would be misleading — so the panel goes
        // to its decided state rather than claiming a review is under way.
        this.#status('Your application has already been reviewed. Please contact us if you have questions.', 'success');
        this.#showReceived('decided');
        return;
      }

      if (result.alreadyPending) {
        // Thanking them again reads as though the first submission was lost.
        // The answers were still refreshed; this names the way to a live view.
        this.#status(
          'You have already applied with this address. Sign in with it to check your status.',
          'success',
        );
        this.#remember();
        this.#showReceived(result.status || 'pending');
        this.emit('membership:applied');
        return;
      }

      this.#remember();
      this.#status('Thank you. Your application has been received.', 'success');
      this.#showReceived(result.status || 'pending');
      this.emit('membership:applied');
    } catch {
      this.#status('Could not reach the server. Please try again.', 'error');
    } finally {
      this.#setBusy(false);
    }
  }

  /**
   * Fill State / Region from the chosen country's `data-provinces`, which
   * `all_country_option_tags` already carries. Nothing is hardcoded, the key
   * stays `state`, and the limitation for a country Shopify lists no
   * subdivisions for is stated in blocks/apply-form.liquid.
   */
  #syncState() {
    const country = /** @type {HTMLSelectElement | undefined} */ (this.refs['country']);
    const state = /** @type {HTMLSelectElement | undefined} */ (this.refs['state']);
    if (!country || !state) return;
    let list;
    // An empty select is still submittable: the field is optional.
    try { list = JSON.parse(country.selectedOptions[0]?.dataset.provinces || '[]'); } catch { list = []; }
    // The placeholder is moved, not rebuilt, so its wording survives.
    const placeholder = state.options[0];
    const previous = state.value;
    state.replaceChildren();
    if (placeholder) state.append(placeholder);
    if (Array.isArray(list)) {
      for (const entry of list) {
        // [value, label] pairs, which DIFFER for a few ("District of Columbia"
        // / "Washington DC"). The canonical value is what gets stored.
        const pair = Array.isArray(entry) ? entry : [entry, entry];
        if (!pair[0]) continue;
        const option = document.createElement('option');
        option.value = String(pair[0]);
        option.textContent = String(pair[1] ?? pair[0]);
        state.append(option);
      }
    }
    // Keep an already-chosen region where the new list still offers it.
    state.value = previous;
    if (state.selectedIndex < 0) state.selectedIndex = 0;
  }

  /**
   * @param {string} ref
   * @returns {string}
   */
  #value(ref) {
    const el = /** @type {HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | undefined} */ (this.refs[ref]);
    return el?.value?.trim() ?? '';
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    this.#busy = busy;
    const button = /** @type {HTMLButtonElement | undefined} */ (this.refs['submit']);
    if (button) button.disabled = busy;
  }

  /**
   * @param {string} message
   * @param {'pending' | 'error' | 'success'} tone
   */
  #status(message, tone) {
    const el = /** @type {HTMLElement | undefined} */ (this.refs['status']);
    if (!el) return;
    // textContent, never innerHTML.
    el.textContent = message;
    el.dataset.tone = tone;
  }
}

if (!customElements.get('apply-form')) {
  customElements.define('apply-form', ApplyForm);
}
